/**
 * Login and password recovery.
 *
 * Setup derives the username instead of asking for it, so on the normal
 * single-account device this screen never shows one: it greets the driver by
 * name and asks only for the password. The field reappears the moment a second
 * account exists, which is the only case where it is a real choice.
 */

import { useEffect, useState, type FormEvent } from 'react';
import {
  assessPassword,
  resetPasswordWithRecoveryCode,
  type AccountRecord,
} from '@/crypto/vault';
import { accountsDb, openAccountDb } from '@/db/schema';
import { appendAudit } from '@/db/audit';
import { listAccounts, unlockAccount } from '@/session/SessionContext';
import { Callout, Field, Select, TextInput } from '@/components/ui';
import { useT } from '@/i18n';
import { AuthShell } from './SetupScreen';

interface LoginScreenProps {
  onUnlocked: (state: { account: AccountRecord; dataKey: CryptoKey }) => void;
  /** Back to the sign-in-or-register choice. */
  onBack: () => void;
}

type Mode = 'login' | 'recover';

export function LoginScreen({ onUnlocked, onBack }: LoginScreenProps) {
  const t = useT();
  const [mode, setMode] = useState<Mode>('login');
  const [accounts, setAccounts] = useState<AccountRecord[]>([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const list = await listAccounts();
      setAccounts(list);
      if (list.length > 0 && list[0]) setUsername(list[0].username);
    })();
  }, []);

  const sole = accounts.length === 1 ? accounts[0] : undefined;

  async function handleLogin(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const unlocked = await unlockAccount(username, password);
      onUnlocked(unlocked);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.login.failed);
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  if (mode === 'recover') {
    return (
      <RecoverScreen
        accounts={accounts}
        initialUsername={username}
        onCancel={() => setMode('login')}
        onUnlocked={onUnlocked}
      />
    );
  }

  return (
    <AuthShell
      title={t.login.title}
      subtitle={sole ? t.login.greeting(sole.displayName) : t.login.subtitle}
    >
      <form onSubmit={handleLogin} className="space-y-4">
        {accounts.length > 1 && (
          <Field label={t.login.driver}>
            <Select value={username} onChange={(event) => setUsername(event.target.value)}>
              {accounts.map((account) => (
                <option key={account.id} value={account.username}>
                  {account.displayName} ({account.driverCode})
                </option>
              ))}
            </Select>
          </Field>
        )}

        {accounts.length === 0 && (
          <Field label={t.login.username}>
            <TextInput
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              autoCapitalize="none"
            />
          </Field>
        )}

        <Field label={t.login.password}>
          <TextInput
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            autoFocus
          />
        </Field>

        {error && <Callout tone="danger">{error}</Callout>}

        <button type="submit" className="btn-primary w-full" disabled={busy || !password}>
          {busy ? t.login.decrypting : t.login.signIn}
        </button>

        <button
          type="button"
          className="btn-ghost w-full text-xs"
          onClick={() => setMode('recover')}
        >
          {t.login.forgot}
        </button>

        <button type="button" className="btn-ghost w-full text-xs" onClick={onBack}>
          {t.welcome.back}
        </button>
      </form>
    </AuthShell>
  );
}

function RecoverScreen({
  accounts,
  initialUsername,
  onCancel,
  onUnlocked,
}: {
  accounts: AccountRecord[];
  initialUsername: string;
  onCancel: () => void;
  onUnlocked: (state: { account: AccountRecord; dataKey: CryptoKey }) => void;
}) {
  const t = useT();
  const [username, setUsername] = useState(initialUsername);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newCode, setNewCode] = useState<string | null>(null);
  const [pending, setPending] = useState<{ account: AccountRecord; dataKey: CryptoKey } | null>(
    null,
  );

  const strength = assessPassword(password);

  async function handleReset(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (password !== confirmation) {
      setError(t.setup.passwordsDiffer);
      return;
    }
    if (password.length < 12) {
      setError(t.setup.passwordTooShort);
      return;
    }

    setBusy(true);
    try {
      const account = await accountsDb.accounts
        .where('username')
        .equals(username.trim().toLowerCase())
        .first();
      if (!account) throw new Error(t.login.unknownUser);

      const result = await resetPasswordWithRecoveryCode(account, code, password);
      await accountsDb.accounts.put(result.account);
      openAccountDb(result.account.dbName);
      await appendAudit(
        { dataKey: result.dataKey, driverId: result.account.id },
        {
          action: 'update',
          entityType: 'system',
          entityId: result.account.id,
          entityLabel: 'Passwort über Wiederherstellungscode zurückgesetzt',
          changes: [],
          reason: 'Passwort vergessen',
        },
      );

      // A new code is issued so the old slip of paper stops being a credential.
      setNewCode(result.recoveryCode);
      setPending({ account: result.account, dataKey: result.dataKey });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.login.resetFailed);
    } finally {
      setBusy(false);
    }
  }

  if (newCode && pending) {
    return (
      <AuthShell title={t.login.newCodeTitle} subtitle={t.login.newCodeSubtitle}>
        <div className="border-line-strong bg-subtle rounded-xl border p-5 text-center">
          <p className="text-heading font-mono text-lg tracking-widest break-all">{newCode}</p>
        </div>
        <button
          type="button"
          className="btn-secondary mt-3 w-full"
          onClick={() => void navigator.clipboard?.writeText(newCode)}
        >
          {t.setup.copy}
        </button>
        <button
          type="button"
          className="btn-primary mt-3 w-full"
          onClick={() => onUnlocked(pending)}
        >
          {t.login.notedContinue}
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t.login.recoverTitle} subtitle={t.login.recoverSubtitle}>
      <form onSubmit={handleReset} className="space-y-4">
        {accounts.length > 1 && (
          <Field label={t.login.driver}>
            <Select value={username} onChange={(event) => setUsername(event.target.value)}>
              {accounts.map((account) => (
                <option key={account.id} value={account.username}>
                  {account.displayName}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label={t.login.code} hint={t.login.codeHint}>
          <TextInput
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
            className="font-mono"
            autoCapitalize="characters"
          />
        </Field>

        <Field
          label={t.login.newPassword}
          hint={password ? t.login.strength(strength.label) : t.login.minChars}
        >
          <TextInput
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
          />
        </Field>

        <Field label={t.login.newPasswordRepeat}>
          <TextInput
            type="password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoComplete="new-password"
          />
        </Field>

        {error && <Callout tone="danger">{error}</Callout>}

        <button type="submit" className="btn-primary w-full" disabled={busy || !code}>
          {busy ? t.login.checking : t.login.reset}
        </button>
        <button type="button" className="btn-ghost w-full text-xs" onClick={onCancel}>
          {t.login.backToLogin}
        </button>
      </form>
    </AuthShell>
  );
}
